import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
  TransferState,
} from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withIncrementalHydration,
} from '@angular/platform-browser';
import {
  PreloadAllModules,
  provideRouter,
  Router,
  RouteReuseStrategy,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideApiConfiguration } from '@api/api-configuration';
import { Language } from '@api/models';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { environment } from '../environments/environment';
import { ALL_ROUTES } from './app.routes';
import { DynamicSlugRouteReuseStrategy } from './route-reuse.strategy';
import { isTransferCacheable } from './helpers/is-transfer-cacheable.function';
import { initLanguage } from './initializers/language.initializer';
import { initMaintenance } from './initializers/maintenance.initializer';
import { initRouteResolver } from './initializers/route-resolver.initializer';
import { langInterceptor } from './interceptors/lang.interceptor';
import { maintenanceInterceptor } from './interceptors/maintenance.interceptor';
import { requestSourceInterceptor } from './interceptors/request-source.interceptor';
import { sessionEndedInterceptor } from '@/interceptors/session-ended.interceptor';

import { TranslateLoaderBrowser } from './services/language/translate-loader.browser';
import { ChunkLoadDelegatingErrorHandler } from './services/version-check/chunk-load-error.handler';
import { versionCheckOnErrorInterceptor } from './services/version-check/version-check-on-error.interceptor';
import { VersionCheckService } from './services/version-check/version-check.service';

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: LOCALE_ID,
      useValue: Language.UK,
    },
    provideClientHydration(
      withEventReplay(),
      withIncrementalHydration(),
      withHttpTransferCacheOptions({
        // Both of these are refusals Angular applies to every request this app makes — see
        // `isTransferCacheable`, which takes over the job of deciding what is safe to embed.
        includeRequestsWithAuthHeaders: true,
        includeNonCacheableRequests: true,
        filter: isTransferCacheable,
      }),
    ),
    provideAppInitializer(initLanguage()),
    provideAppInitializer(initRouteResolver()),
    provideAppInitializer(initMaintenance()),
    provideAppInitializer(() => {
      inject(VersionCheckService);
    }),
    provideAppInitializer(() => { inject(Sentry.TraceService); }),
    provideHttpClient(
      withInterceptors([
        requestSourceInterceptor,
        versionCheckOnErrorInterceptor,
        langInterceptor,
        maintenanceInterceptor,
        sessionEndedInterceptor,
      ]),
    ),
    provideZonelessChangeDetection(),
    { provide: RouteReuseStrategy, useClass: DynamicSlugRouteReuseStrategy },
    provideRouter(
      ALL_ROUTES,
      withRouterConfig({ onSameUrlNavigation: 'reload', paramsInheritanceStrategy: 'emptyOnly' }),
      withPreloading(PreloadAllModules),
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateLoaderBrowser,
        deps: [HttpClient, TransferState],
      },
      lang: null,
      fallbackLang: null,
    }),
    provideEnvironmentNgxMask({ validation: false }),
    {
      provide: ErrorHandler,
      useClass: ChunkLoadDelegatingErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideApiConfiguration(environment.apiHost),
  ],
};
